import { useEffect, useRef, useState } from "react";
import { Button, Grid, IconButton, Typography } from "@mui/material";
import { Add as AddIcon, DeleteOutline } from "@mui/icons-material";
import { isEqual, map, some } from "lodash";
import AddressFields from "./AddressFields";
import { AddressLocationTypeEnum } from "@/graphql/generated/graphql";
import { AddressType } from "@/types/globals";
import { arrayLength } from "@/utils/helperFunctions";

const DEFAULT_ADDRESS = { street: '', city: '', state: '', country: 'United Kingdom', zip: '', title: '' }

export const isAddressComplete = (address: AddressType) =>
  Boolean(address?.street?.trim() && address?.city?.trim() && address?.state?.trim() && address?.zip?.trim());

const makeEmptyAddress = (isMain: boolean): AddressType => ({
  ...DEFAULT_ADDRESS,
  locationType: isMain ? AddressLocationTypeEnum.Main : AddressLocationTypeEnum.Sub,
});

interface AddressesFormProps {
  onChange: (addresses: AddressType[]) => void;
  addresses?: AddressType[];
  multiple?: boolean;
}

const NewAddressesForm: React.FC<AddressesFormProps> = ({ onChange, addresses: initialAddresses, multiple = false }) => {
  const [addresses, setAddresses] = useState<AddressType[]>(() =>
    arrayLength(initialAddresses) > 0 ? (initialAddresses as AddressType[]) : [makeEmptyAddress(true)]
  );

  useEffect(() => {
    if (arrayLength(initialAddresses) > 0) {
      setAddresses((currentAddresses) =>
        isEqual(currentAddresses, initialAddresses) ? currentAddresses : (initialAddresses as AddressType[])
      );
    }
  }, [initialAddresses]);

  // Stable identities for list rendering so add/remove doesn't remount
  // the remaining forms and wipe their touched state
  const keyCounter = useRef(0);
  const keysRef = useRef<number[]>([]);
  while (keysRef.current.length < arrayLength(addresses)) keysRef.current.push(keyCounter.current++);

  const update = (next: AddressType[]) => {
    setAddresses(next);
    onChange(next);
  };

  const handleFieldChange = (index: number, key: string, value: string) => {
    update(map(addresses, (address, i) => (i === index ? { ...address, [key]: value } : address)));
  };

  const handleAdd = () => {
    keysRef.current.push(keyCounter.current++);
    const hasMain = some(addresses, (address) => address.locationType === AddressLocationTypeEnum.Main);
    update([...addresses, makeEmptyAddress(!hasMain)]);
  };

  const handleRemove = (index: number) => {
    const removingMain = addresses[index]?.locationType === AddressLocationTypeEnum.Main;
    keysRef.current = [...keysRef.current.slice(0, index), ...keysRef.current.slice(index + 1)];

    const next = addresses.filter((_, i) => i !== index);
    if (removingMain && arrayLength(next) > 0) {
      next[0] = { ...next[0], locationType: AddressLocationTypeEnum.Main };
    }
    update(next);
  };

  const hasIncompleteAddress = some(addresses, (address) => !isAddressComplete(address));

  return (
    <Grid container size={12} spacing={2}>
      {map(addresses, (address, index) => (
        <Grid container size={12} key={keysRef.current[index]}>
          {multiple && (
            <Grid container size={12} alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" fontWeight={700}>
                {address.locationType === AddressLocationTypeEnum.Main
                  ? "Primary Address"
                  : `Address ${index + 1}`}
              </Typography>
              {arrayLength(addresses) > 1 && (
                <IconButton
                  onClick={() => handleRemove(index)}
                  color="error"
                  size="small"
                  aria-label={`Remove address ${index + 1}`}
                >
                  <DeleteOutline />
                </IconButton>
              )}
            </Grid>
          )}
          <AddressFields
            address={address}
            onFieldChange={(key, value) => handleFieldChange(index, key, value)}
          />
        </Grid>
      ))}

      {multiple && (
        <Grid size={{ xs: 12, sm: 6 }}>
          <Button
            fullWidth
            type="button"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={hasIncompleteAddress}
          >
            {arrayLength(addresses) > 1 ? "Add Another Address" : "Add Address"}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

export default NewAddressesForm;
